import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import scheduleManagementApi from '../../api/scheduleManagementApi';
import { HandleRequestData } from '../../../common/interfaces/handleRequest';
import { PropertyFilterProps } from '@amzn/awsui-components-react-v3';
import { ActivityGroupListResponseData } from '../../interfaces/ActivityGroupListResponseData';
import {
    ActivityGroupRecord,
    ActivityGroupListColumnLabels,
} from '../../interfaces/activityGroup';
import {
    selectUserId,
    selectActivityGroupListPreference,
    updatePreferencesAndSavedFilters,
} from '../../../common/store/slices/userSlice';

export const defaultVisibleColumns = [...ActivityGroupListColumnLabels];

export interface ActivityGroupListState {
    activityGroups: ActivityGroupRecord[];
    totalActivityGroups: number;
    isLoading: boolean;
    error: string | null;
    nextToken: string | null;
    visibleColumns: Array<string> | undefined;
    wrapLines: boolean;
    contentDensity: 'comfortable' | 'compact';
    stripedRows: boolean;
}

export const fetchActivityGroups = createAsyncThunk<
    {
        activity_groups: ActivityGroupRecord[];
        total_activity_groups: number;
        next_token: string | null;
    },
    PropertyFilterProps.Query | undefined,
    { rejectValue: string }
>(
    'activityGroupList/fetchActivityGroups',
    async (query, { rejectWithValue }) => {
        if (query && query.tokens && query.tokens.length === 0) {
            query = undefined;
        }

        try {
            const response: HandleRequestData<ActivityGroupListResponseData> =
                await scheduleManagementApi.getActivityGroupList(query);
            if (response.status === 200) {
                return {
                    activity_groups: response.result.activity_groups,
                    total_activity_groups:
                        response.result.total_activity_groups,
                    next_token: response.result.next_token || null,
                };
            } else {
                return rejectWithValue(
                    `Failed to fetch activity groups. Status: ${response.status}`,
                );
            }
        } catch (error: any) {
            return rejectWithValue(
                `Failed to fetch activity groups with current Filter`,
            );
        }
    },
);

const activityGroupListSlice = createSlice({
    name: 'activityGroupList',
    initialState: {
        activityGroups: [],
        totalActivityGroups: 0,
        isLoading: false,
        error: null,
        nextToken: null,
        visibleColumns: defaultVisibleColumns,
        wrapLines: false,
        contentDensity: 'comfortable',
        stripedRows: false,
    } as ActivityGroupListState,
    reducers: {
        setVisibleColumns: (
            state,
            action: PayloadAction<Array<string> | undefined>,
        ) => {
            if (action.payload && action.payload.length > 0) {
                state.visibleColumns = action.payload;
            } else {
                state.visibleColumns = defaultVisibleColumns;
            }
        },
        setWrapLines: (state, action: PayloadAction<boolean>) => {
            state.wrapLines = action.payload;
        },
        setContentDensity: (
            state,
            action: PayloadAction<'comfortable' | 'compact'>,
        ) => {
            state.contentDensity = action.payload;
        },
        setStripedRows: (state, action: PayloadAction<boolean>) => {
            state.stripedRows = action.payload;
        },
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchActivityGroups.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(
                fetchActivityGroups.fulfilled,
                (
                    state,
                    action: PayloadAction<{
                        activity_groups: ActivityGroupRecord[];
                        total_activity_groups: number;
                        next_token: string | null;
                    }>,
                ) => {
                    state.isLoading = false;
                    state.activityGroups = action.payload.activity_groups;
                    state.totalActivityGroups =
                        action.payload.total_activity_groups;
                    state.nextToken = action.payload.next_token;
                },
            )
            .addCase(fetchActivityGroups.rejected, (state, action) => {
                state.isLoading = false;
                state.activityGroups = [];
                state.totalActivityGroups = 0;
                state.error = action.payload || 'An error occurred';
                state.nextToken = null;
            });
    },
});

export default activityGroupListSlice.reducer;

export const {
    setVisibleColumns,
    setWrapLines,
    setContentDensity,
    setStripedRows,
    setIsLoading,
} = activityGroupListSlice.actions;

export const initializeUserPreference = () => {
    return (dispatch: any, getState: any) => {
        const preference = selectActivityGroupListPreference(getState());

        if (preference?.visible_columns) {
            dispatch(setVisibleColumns(preference.visible_columns));
        }
        if (preference?.wrap_lines !== undefined) {
            dispatch(setWrapLines(preference.wrap_lines));
        }
        if (preference?.content_density) {
            dispatch(setContentDensity(preference.content_density));
        }
        if (preference?.striped_rows !== undefined) {
            dispatch(setStripedRows(preference.striped_rows));
        }
    };
};

export const updateUserPreference = (preference: {
    visible_columns?: Array<string>;
    page_size?: number;
    wrap_lines?: boolean;
    content_density?: 'comfortable' | 'compact';
    striped_rows?: boolean;
}) => {
    return async (dispatch: any, getState: any) => {
        const userId = selectUserId(getState());

        if (userId && preference) {
            return dispatch(
                updatePreferencesAndSavedFilters(userId, {
                    preferences: {
                        activity_group_list: preference,
                    },
                }),
            );
        }
        return Promise.resolve(false);
    };
};

export const selectActivityGroups = (state: {
    activityGroupList: ActivityGroupListState;
}) => state.activityGroupList.activityGroups;
export const selectTotalActivityGroups = (state: {
    activityGroupList: ActivityGroupListState;
}) => state.activityGroupList.totalActivityGroups;
export const selectIsLoading = (state: {
    activityGroupList: ActivityGroupListState;
}) => state.activityGroupList.isLoading;
export const selectError = (state: {
    activityGroupList: ActivityGroupListState;
}) => state.activityGroupList.error;
export const selectNextToken = (state: {
    activityGroupList: ActivityGroupListState;
}) => state.activityGroupList.nextToken;
export const selectVisibleColumns = (state: {
    activityGroupList: ActivityGroupListState;
}) => state.activityGroupList.visibleColumns;
export const selectWrapLines = (state: {
    activityGroupList: ActivityGroupListState;
}) => state.activityGroupList.wrapLines;
export const selectContentDensity = (state: {
    activityGroupList: ActivityGroupListState;
}) => state.activityGroupList.contentDensity;
export const selectStripedRows = (state: {
    activityGroupList: ActivityGroupListState;
}) => state.activityGroupList.stripedRows;
